import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  Input,
  OnDestroy,
  signal,
  untracked,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatIconButton } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { toolbarOptions } from "../../shared/model/toolbar-constants";
import { MapActionsService } from "../../shared/services/map-actions.service";
import { ToolbarService } from "../../viewer-components/services/internal-services/toolbar.service";
import { ToastService } from "../../viewer-components/services/internal-services/toast.service";
import { TOAST_STATUS } from "../../viewer-components/model/ToastConstants";
import { CustomElementsPanelComponent } from "../../compound-components/custom-elements-detection/custom-elements-panel.component";
import { ProcessingOverlayComponent } from "../../compound-components/custom-elements-detection/panels/processing-overlay/processing-overlay.component";
import { DetectionService } from "../../compound-components/services/detection/detection.service";
import { DetectionCommunicatorService } from "../../compound-components/services/detection/detection.communicator.service";
import { DetectionsLayerService } from "../../compound-components/services/layers";
import {
  CustomElementsLaunchRequest,
  CustomElementsLauncherService,
} from "../../compound-components/services/custom-elements-launcher.service";
import { ConfirmBackToConfigDialogComponent } from "../../compound-components/custom-elements-detection/panels/results-panel/confirm-back-to-config-dialog.component";

/**
 * Left-sidebar toolbar component for Custom Elements Detection.
 * Provides DetectionService so both the panel and the processing overlay
 * share the same instance. Also syncs the detection bbox layer on the map
 * with the detection state via an effect.
 */
@Component({
  selector: "app-custom-elements-toolbar",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatIconButton, MatTooltip, CustomElementsPanelComponent, ProcessingOverlayComponent],
  templateUrl: "./custom-elements-toolbar.component.html",
  styleUrl: "./custom-elements-toolbar.component.scss",
  providers: [DetectionService, DetectionCommunicatorService, DetectionsLayerService],
})
export class CustomElementsToolbarComponent implements OnDestroy {
  private readonly toolbarService = inject(ToolbarService);

  private readonly detectionsLayer = inject(DetectionsLayerService);

  private readonly launcher = inject(CustomElementsLauncherService);

  private readonly destroyRef = inject(DestroyRef);

  private readonly mapActionsService = inject(MapActionsService);

  private readonly toast = inject(ToastService);

  private readonly dialog = inject(MatDialog);

  protected readonly detection = inject(DetectionService);

  readonly toolbarOption = toolbarOptions.CUSTOM_ELEMENTS_DETECTION;

  displayPanel = false;

  /**
   * Mirrors the map's 2D/3D mode as a signal (set from the `is3DMode` input) so the
   * layer-drawing effects below can react to it. In 3D, detection layers are hidden
   * (not cleared) — the state in {@link DetectionService} is untouched, so switching
   * back to 2D re-triggers these same effects and redraws everything from it.
   */
  protected readonly is3D = signal(false);

  constructor() {
    // Rebuild layers when data changes (stage, objects, results, global visibility,
    // 3D mode). bboxVisibility/maskVisibility are read with untracked so their changes
    // don't trigger a full rebuild — the two lightweight effects below handle that.
    effect(() => {
      const stage = this.detection.stage();
      const objects = this.detection.objects();
      const results = this.detection.results();
      const visibility = this.detection.visibility();
      const is3D = this.is3D();
      if (stage === "results" && !is3D) {
        this.detectionsLayer.setDetections(
          objects,
          results,
          visibility,
          untracked(() => this.detection.bboxVisibility()),
          untracked(() => this.detection.maskVisibility()),
        );
      } else {
        this.detectionsLayer.clearDetections();
      }
    });

    // Toggle bbox layer visibility per object — no feature rebuild needed.
    effect(() => {
      const bv = this.detection.bboxVisibility();
      for (const obj of this.detection.objects()) {
        this.detectionsLayer.updateBboxVisibility(obj.name, bv[obj.name] !== false);
      }
    });

    // Toggle mask layer visibility per object — no feature rebuild needed.
    effect(() => {
      const mv = this.detection.maskVisibility();
      for (const obj of this.detection.objects()) {
        this.detectionsLayer.updateMaskVisibility(obj.name, mv[obj.name] !== false);
      }
    });

    // Debug overlay: draw pre-merge fragment bboxes when Alt+0 is toggled.
    effect(() => {
      const debug = this.detection.debugMergedVisible();
      const stage = this.detection.stage();
      const is3D = this.is3D();
      if (stage !== "results" || is3D) {
        this.detectionsLayer.clearDebugMergedFragments();
        return;
      }
      this.detectionsLayer.setDebugMergedFragments(
        untracked(() => this.detection.objects()),
        untracked(() => this.detection.results()),
        debug,
      );
    });

    // Debug overlay: draw the tile grid the current area would be split into.
    // debugTileWkts runs a real per-tile turf intersection (GeoService.tileArea) — only
    // read it while the overlay is actually visible, otherwise this effect would recompute
    // it on every area/zoom change (e.g. every vertex while drawing) for no visible benefit,
    // which is expensive enough to stall the browser on a large drawn area.
    effect(() => {
      const debug = this.detection.debugTilesVisible();
      const is3D = this.is3D();
      if (!debug || is3D) {
        this.detectionsLayer.setDebugTiles([], false);
        return;
      }
      const tileWkts = this.detection.debugTileWkts();
      this.detectionsLayer.setDebugTiles(tileWkts, true);
    });

    // Sync the detection-area outline on the map.
    // The OL draw tool only shows the polygon while the user is interactively
    // drawing; this layer makes the area visible persistently — critical for
    // imports (file / search-results bridge) which never go through the draw
    // tool and would otherwise leave the map with no visual feedback.
    // Hidden (not cleared) in 3D — same reasoning as the main layer-rebuild effect.
    effect(() => {
      const wkt = this.detection.area().geometryWkt;
      const is3D = this.is3D();
      if (wkt && !is3D) {
        this.detectionsLayer.setArea(wkt);
      } else {
        this.detectionsLayer.clearArea();
      }
    });

    // Sync map feature clicks to panel scroll — mirrors the search scroll pattern.
    this.detectionsLayer.featureClick$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((click) => {
      this.detection.setScrollToDetection(click);
    });

    // Cross-feature bridge: when another feature (e.g. embeddings search)
    // requests launching detection on a pre-built area, open this toolbar,
    // apply the area, and jump straight to the configure stage.
    // If detection results already exist, ask for confirmation first since they will be discarded.
    this.launcher.launchRequest$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((req) => {
      if (this.detection.totalDetections() === 0) {
        this.applyLaunchRequest(req);
        return;
      }
      this.dialog
        .open(ConfirmBackToConfigDialogComponent, {
          width: "410px",
          maxWidth: "95vw",
          autoFocus: false,
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((confirmed) => {
          if (confirmed) {
            this.applyLaunchRequest(req);
          }
        });
    });
  }

  private applyLaunchRequest(req: CustomElementsLaunchRequest): void {
    if (this.is3D()) {
      // The toolbar stays mounted across 2D/3D switches (only its button/panel hide), so
      // launch requests still arrive here — but detection is a 2D-only feature. Inform
      // instead of applying: the state would be invisible until the user switches back.
      this.toast.setNotification({
        id: "detection-3d-handoff",
        message: "Custom Elements detection is only available in 2D mode.",
        type: TOAST_STATUS.INFO,
        timeout: 5000,
        positionClass: "toast-top-right",
        closeButton: true,
      });
      return;
    }
    // Flush any shape previously drawn with the map selection tools — the draw tool keeps
    // it on its own layer, so replacing the area in DetectionService alone leaves it visible.
    this.mapActionsService.requestGeofencingSelection({ id: null, type: null });
    this.detection.setArea({
      mode: "search",
      geometryWkt: req.visualWkt ?? req.areas[0]?.wkt,
      areas: req.areas,
      importedFile: { name: req.label ?? "Imported area", sizeBytes: 0, featureCount: req.areas.length },
    });
    if (req.results) {
      // Agent handoff (viewer-handoff plan F3.3): results are pre-computed — skip the
      // configure/run flow and land straight in the results stage.
      this.detection.loadResults(req.results);
    } else {
      this.detection.resetForNewRun();
    }
    this.toolbarService.enableTool(this.toolbarOption);
  }

  /** Drives panel visibility from the shared toolbar state. */
  @Input() set enabledTool(tool: string) {
    this.displayPanel = this.toolbarOption === tool;
  }

  /**
   * Whether the map is currently in 3D (oblique) mode. The toolbar itself stays mounted
   * across 2D/3D switches (see `map-container.component.html`) — only its button/panel are
   * hidden here, and detection map layers are hidden rather than cleared, so `DetectionService`
   * state and results survive the round trip and reappear when returning to 2D.
   */
  @Input() set is3DMode(value: boolean) {
    this.is3D.set(!!value);
  }

  togglePanelDisplay(): void {
    this.toolbarService.enableTool(!this.displayPanel ? this.toolbarOption : toolbarOptions.NONE);
  }

  ngOnDestroy(): void {
    this.detectionsLayer.clearDetections();
    this.detectionsLayer.clearArea();
    this.detectionsLayer.clearDebugTiles();
  }
}
